/**
 * <s-gallery-dots> — Marks the dot for the gallery image currently in view.
 *
 * Required child markup:
 *   [data-gallery-slide][id]  — Each gallery image wrapper.
 *   [data-gallery-dot="<id>"] — Dot link pointing at a slide id; gets aria-current="true" while its slide is in view.
 */
class SGalleryDots extends HTMLElement {
  #observer;
  #swipeObserver;

  connectedCallback() {
    const slides = this.querySelectorAll('[data-gallery-slide]');
    const dots = this.querySelectorAll('[data-gallery-dot]');
    if (!slides.length || !dots.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          for (const dot of dots) {
            if (dot.dataset.galleryDot === entry.target.id) {
              dot.setAttribute('aria-current', 'true');
            } else {
              dot.removeAttribute('aria-current');
            }
          }
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    slides.forEach((slide) => this.#observer.observe(slide));

    // Phones: the gallery swipes sideways, so also watch which slide fills the swipe strip.
    const strip = this.querySelector('[data-product-gallery]');
    if (strip) {
      this.#swipeObserver = new IntersectionObserver(
        (entries) => {
          if (strip.scrollWidth <= strip.clientWidth + 1) return;
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            for (const dot of dots) {
              if (dot.dataset.galleryDot === entry.target.id) dot.setAttribute('aria-current', 'true');
              else dot.removeAttribute('aria-current');
            }
          }
        },
        { root: strip, threshold: 0.6 }
      );
      slides.forEach((slide) => this.#swipeObserver.observe(slide));
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#swipeObserver?.disconnect();
  }
}

if (!customElements.get('s-gallery-dots')) {
  customElements.define('s-gallery-dots', SGalleryDots);
}
